// Admin TopBar Component
import React, { useState, useEffect, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiBell, FiUser, FiSettings, FiLogOut, FiUsers } from 'react-icons/fi';
import SettingsSidebar from './SettingsSidebar';

const TopBar: React.FC = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettingsSidebar, setShowSettingsSidebar] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'profile' | 'password' | 'users'>('profile');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Get random avatar from UIAvatars.com
  const getAvatarUrl = (name: string) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff&size=128`;
  };

  // Debounced search function
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  useEffect(() => {
    // Get user from localStorage
    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [contactRes, volunteerRes, donationsRes] = await Promise.all([
        fetch('http://localhost:5000/api/contact?status=unread'),
        fetch('http://localhost:5000/api/volunteers/applications?status=pending'),
        fetch('http://localhost:5000/api/donations?status=pending')
      ]);

      const contactData = await contactRes.json();
      const volunteerData = await volunteerRes.json();
      const donationsData = await donationsRes.json();

      const contactNotifications = (contactData.messages || []).map((msg: any) => ({
        id: `contact-${msg.id}`,
        type: 'contact',
        title: `New message from ${msg.full_name}`,
        message: msg.subject,
        time: msg.created_at,
        data: msg
      }));

      const volunteerNotifications = (volunteerData.applications || []).map((vol: any) => ({
        id: `volunteer-${vol.id}`,
        type: 'volunteer',
        title: `New volunteer application`,
        message: `${vol.full_name} applied to volunteer`,
        time: vol.created_at,
        data: vol
      }));

      const donationNotifications = (donationsData.donations || []).map((donation: any) => ({
        id: `donation-${donation.id}`,
        type: 'donation',
        title: `New donation submission`,
        message: `${donation.full_name} pledged $${parseFloat(donation.amount).toFixed(2)}`,
        time: donation.created_at,
        data: donation
      }));

      const allNotifications = [...contactNotifications, ...volunteerNotifications, ...donationNotifications]
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 10);

      setNotifications(allNotifications);
      setUnreadCount(allNotifications.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (notification.type === 'contact') {
      navigate('/admin/messages', { state: { openMessage: notification.data } });
    } else if (notification.type === 'volunteer') {
      navigate('/admin/volunteers');
    } else if (notification.type === 'donation') {
      navigate('/admin/donors', { state: { openDonor: notification.data } });
    }
    setShowNotifications(false);
    // Refresh notifications immediately
    fetchNotifications();
  };

  const performSearch = async (query: string) => {
    setSearchLoading(true);
    try {
      // Search across multiple endpoints
      const [teamRes, childrenRes, volunteersRes, donorsRes, contactRes] = await Promise.all([
        fetch(`http://localhost:5000/api/team`),
        fetch(`http://localhost:5000/api/children`),
        fetch(`http://localhost:5000/api/volunteers/applications`),
        fetch(`http://localhost:5000/api/donations`),
        fetch(`http://localhost:5000/api/contact`)
      ]);

      const [teamData, childrenData, volunteersData, donorsData, contactData] = await Promise.all([
        teamRes.json(),
        childrenRes.json(),
        volunteersRes.json(),
        donorsRes.json(),
        contactRes.json()
      ]);

      const results: any[] = [];
      const lowerQuery = query.toLowerCase();

      // Search in team members
      const teamMembers = (Array.isArray(teamData) ? teamData : []).filter((member: any) =>
        member.name?.toLowerCase().includes(lowerQuery) ||
        member.position?.toLowerCase().includes(lowerQuery)
      );
      if (teamMembers.length > 0) {
        results.push({
          category: 'Team Members',
          items: teamMembers.map((m: any) => ({
            id: m.id,
            title: m.name,
            subtitle: m.position,
            path: '/admin/team'
          }))
        });
      }

      // Search in children
      const children = (Array.isArray(childrenData) ? childrenData : []).filter((child: any) =>
        child.name?.toLowerCase().includes(lowerQuery) ||
        child.age?.toString().includes(lowerQuery)
      );
      if (children.length > 0) {
        results.push({
          category: 'Children',
          items: children.map((c: any) => ({
            id: c.id,
            title: c.name,
            subtitle: `Age: ${c.age}`,
            path: '/admin/children'
          }))
        });
      }

      // Search in volunteers
      const volunteers = (volunteersData.applications || []).filter((vol: any) =>
        vol.full_name?.toLowerCase().includes(lowerQuery) ||
        vol.email?.toLowerCase().includes(lowerQuery)
      );
      if (volunteers.length > 0) {
        results.push({
          category: 'Volunteers',
          items: volunteers.map((v: any) => ({
            id: v.id,
            title: v.full_name,
            subtitle: v.email,
            path: '/admin/volunteers'
          }))
        });
      }

      // Search in donors
      const donors = (donorsData.donations || []).filter((donor: any) =>
        donor.full_name?.toLowerCase().includes(lowerQuery) ||
        donor.email?.toLowerCase().includes(lowerQuery)
      );
      if (donors.length > 0) {
        results.push({
          category: 'Donors',
          items: donors.map((d: any) => ({
            id: d.id,
            title: d.full_name,
            subtitle: `$${d.amount} - ${d.email}`,
            path: '/admin/donors'
          }))
        });
      }

      // Search in messages
      const messages = (contactData.messages || []).filter((msg: any) =>
        msg.full_name?.toLowerCase().includes(lowerQuery) ||
        msg.email?.toLowerCase().includes(lowerQuery) ||
        msg.subject?.toLowerCase().includes(lowerQuery)
      );
      if (messages.length > 0) {
        results.push({
          category: 'Messages',
          items: messages.map((m: any) => ({
            id: m.id,
            title: m.full_name,
            subtitle: m.subject,
            path: '/admin/messages'
          }))
        });
      }

      setSearchResults(results);
      setShowSearchResults(results.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchResultClick = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const handleUserMenuClick = (item: string) => {
    if (item === 'Logout') {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
    } else if (item === 'Settings') {
      setSettingsInitialTab('profile');
      setShowSettingsSidebar(true);
    } else if (item === 'Profile') {
      setSettingsInitialTab('profile');
      setShowSettingsSidebar(true);
    } else if (item === 'User Management') {
      setSettingsInitialTab('users');
      setShowSettingsSidebar(true);
    }
    setShowUserMenu(false);
  };

  const handleUserUpdate = (updatedUser: any) => {
    setCurrentUser(updatedUser);
  };

  return (
    <>
    <div
      style={{
        height: '66px',
        background: '#FFFFFF',
        borderBottom: '1px solid #E8EDF3',
        boxShadow: '0 1px 0 0 rgba(12,34,57,0.04)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 30px',
        gap: '16px',
        position: 'sticky',
        top: 0,
        zIndex: 999,
      }}
    >
      {/* Search Bar */}
      <div
        style={{
          flex: 1,
          maxWidth: '460px',
          position: 'relative',
        }}
      >
        <input
          type="text"
          placeholder="Search team, children, volunteers, donors…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (searchResults.length > 0) setShowSearchResults(true);
          }}
          style={{
            width: '100%',
            padding: '9px 16px 9px 40px',
            background: '#F2F5FA',
            border: '1.5px solid transparent',
            borderRadius: '24px',
            fontSize: '13px',
            color: '#0C2239',
            transition: 'all 0.18s ease',
            outline: 'none',
          }}
          onFocusCapture={(e) => {
            e.target.style.borderColor = 'rgba(3,168,74,0.35)';
            e.target.style.background = '#FFFFFF';
            e.target.style.boxShadow = '0 0 0 3px rgba(3,168,74,0.08)';
          }}
          onBlur={(e) => {
            setTimeout(() => {
              e.target.style.borderColor = 'transparent';
              e.target.style.background = '#F2F5FA';
              e.target.style.boxShadow = 'none';
              setShowSearchResults(false);
            }, 200);
          }}
        />
        <span style={{
          position: 'absolute',
          left: '14px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          color: '#94A3B8',
        }}>
          {createElement(FiSearch as any, { size: 16 })}
        </span>

        {/* Search Results Dropdown */}
        {showSearchResults && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              maxHeight: '400px',
              overflowY: 'auto',
              background: '#FFFFFF',
              borderRadius: '14px',
              boxShadow: '0 8px 32px rgba(12,34,57,0.13)',
              border: '1px solid #E8EDF3',
              zIndex: 1000,
            }}
          >
            {searchLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#64748B' }}>
                <p style={{ margin: 0, fontSize: '13px' }}>Searching…</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94A3B8' }}>
                <p style={{ margin: 0, fontSize: '13px' }}>No results found</p>
              </div>
            ) : (
              searchResults.map((category, catIndex) => (
                <div key={catIndex}>
                  <div style={{
                    padding: '8px 16px',
                    background: '#F8FAFC',
                    borderBottom: '1px solid #F1F5F9',
                    position: 'sticky', top: 0, zIndex: 1,
                  }}>
                    <p style={{
                      margin: 0, fontSize: '10.5px', fontWeight: '700',
                      color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em',
                    }}>
                      {category.category} ({category.items.length})
                    </p>
                  </div>
                  {category.items.map((item: any, itemIndex: number) => (
                    <button
                      key={itemIndex}
                      onClick={() => handleSearchResultClick(item.path)}
                      style={{
                        width: '100%', padding: '11px 16px',
                        border: 'none', background: 'transparent',
                        textAlign: 'left', cursor: 'pointer',
                        borderBottom: itemIndex < category.items.length - 1 ? '1px solid #F8FAFC' : 'none',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: '600', color: '#0C2239' }}>
                        {item.title}
                      </p>
                      <p style={{ margin: 0, fontSize: '11.5px', color: '#94A3B8' }}>{item.subtitle}</p>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Right Section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
        {/* Notifications */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              width: '38px', height: '38px',
              borderRadius: '10px',
              border: 'none',
              background: '#F2F5FA',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
              transition: 'all 0.18s ease',
              color: '#64748B',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EDF5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#F2F5FA'; }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {createElement(FiBell as any, { size: 18 })}
            </span>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: '5px', right: '5px',
                minWidth: '17px', height: '17px',
                borderRadius: '9px',
                background: '#EF4444',
                border: '2px solid #FFFFFF',
                color: 'white', fontSize: '9px', fontWeight: '700',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px',
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              width: '320px', maxHeight: '420px',
              background: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(12,34,57,0.14)',
              border: '1px solid #E8EDF3',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <h4 style={{ margin: 0, color: '#0C2239', fontSize: '14px', fontWeight: '700' }}>
                  Notifications {unreadCount > 0 && `(${unreadCount})`}
                </h4>
                {unreadCount > 0 && (
                  <button
                    onClick={() => { setShowNotifications(false); navigate('/admin/messages'); }}
                    style={{
                      background: 'none', border: 'none',
                      color: '#1557B0', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    }}
                  >
                    View All
                  </button>
                )}
              </div>
              <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '30px', marginBottom: '8px' }}>🔔</div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#94A3B8' }}>No new notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      style={{
                        padding: '12px 20px', borderBottom: '1px solid #F8FAFC',
                        cursor: 'pointer', transition: 'background 0.15s ease',
                        display: 'flex', gap: '12px',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: '#EF4444', marginTop: '6px', flexShrink: 0,
                      }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '13px', color: '#0C2239', fontWeight: '600' }}>
                          {notification.title}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B' }}>
                          {notification.message}
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94A3B8' }}>
                          {getTimeAgo(notification.time)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <button
          onClick={() => setShowSettingsSidebar(true)}
          style={{
            width: '38px', height: '38px',
            borderRadius: '10px', border: 'none',
            background: '#F2F5FA',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.18s ease', color: '#64748B',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EDF5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#F2F5FA'; }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {createElement(FiSettings as any, { size: 18 })}
          </span>
        </button>

        {/* Divider */}
        <div style={{ width: '1px', height: '28px', background: '#E8EDF3', margin: '0 6px' }} />

        {/* User Profile Chip */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'flex', alignItems: 'center', gap: '9px',
              padding: '5px 12px 5px 5px',
              borderRadius: '24px',
              border: '1.5px solid #E8EDF3',
              background: '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = '#F8FAFC'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8EDF3'; e.currentTarget.style.background = '#FFFFFF'; }}
          >
            <img
              src={
                currentUser?.profileImage
                  ? (currentUser.profileImage.startsWith('/uploads/')
                    ? `http://localhost:5000${currentUser.profileImage}`
                    : currentUser.profileImage)
                  : (currentUser
                    ? getAvatarUrl(currentUser.name)
                    : 'https://ui-avatars.com/api/?name=Admin&background=random&color=fff&size=128')
              }
              alt="User"
              style={{
                width: '30px', height: '30px',
                borderRadius: '50%', objectFit: 'cover',
              }}
            />
            <div style={{ textAlign: 'left' }}>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#0C2239', fontWeight: '600', lineHeight: 1.2 }}>
                {currentUser ? currentUser.name : 'Admin User'}
              </p>
              <p style={{ margin: 0, fontSize: '10.5px', color: '#94A3B8', fontWeight: '500' }}>
                {currentUser?.role === 'admin' ? 'Administrator' : 'Staff'}
              </p>
            </div>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: '2px', color: '#94A3B8' }}>
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* User Dropdown */}
          {showUserMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              width: '200px',
              background: '#FFFFFF',
              borderRadius: '14px',
              boxShadow: '0 8px 32px rgba(12,34,57,0.13)',
              border: '1px solid #E8EDF3',
              overflow: 'hidden',
            }}>
              {[...(currentUser?.username === 'bishal' ? ['User Management'] : []), 'Profile', 'Settings', 'Logout'].map((item) => {
                let icon: any = null;
                if (item === 'Profile')  icon = <span style={{ display: 'flex' }}>{createElement(FiUser    as any, { size: 15 })}</span>;
                if (item === 'Settings') icon = <span style={{ display: 'flex' }}>{createElement(FiSettings as any, { size: 15 })}</span>;
                if (item === 'Logout')   icon = <span style={{ display: 'flex' }}>{createElement(FiLogOut  as any, { size: 15 })}</span>;
                if (item === 'User Management') icon = <span style={{ display: 'flex' }}>{createElement(FiUsers as any, { size: 15 })}</span>;
                return (
                  <button
                    key={item}
                    onClick={() => handleUserMenuClick(item)}
                    style={{
                      width: '100%', padding: '11px 16px',
                      border: 'none', background: 'transparent',
                      textAlign: 'left', cursor: 'pointer',
                      fontSize: '13px', fontWeight: '500',
                      color: item === 'Logout' ? '#EF4444' : '#0C2239',
                      transition: 'background 0.15s ease',
                      display: 'flex', alignItems: 'center', gap: '10px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {icon}{item}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Settings Sidebar */}
    <SettingsSidebar
      isOpen={showSettingsSidebar}
      onClose={() => setShowSettingsSidebar(false)}
      currentUser={currentUser}
      onUserUpdate={handleUserUpdate}
      initialTab={settingsInitialTab}
    />
    </>
  );
};

export default TopBar;
